import type { ProfileInfo } from 'src/redux/profile/types';

type FullStoryModule = typeof import('@fullstory/browser');

// FullStory is loaded via a dynamic import() performed in the init path so it
// stays out of the main chunk (mirrors the Datadog pattern in
// src/app/datadog-init.tsx). The promise is created only once, by
// initializeFullStory(), and shared by every subsequent call.
let fullStoryPromise: Promise<FullStoryModule> | null = null;

/**
 * Starts (or reuses) the dynamic import of the FullStory browser SDK and
 * resolves to the module, or to null if the chunk fails to load. A rejected
 * import is never cached: fullStoryPromise is reset to null so a single failed
 * chunk load does not permanently poison every subsequent call, and a later
 * call can retry the import.
 */
async function loadFullStory(): Promise<FullStoryModule | null> {
  if (!fullStoryPromise) {
    fullStoryPromise = import('@fullstory/browser');
  }

  try {
    return await fullStoryPromise;
  } catch (error) {
    // Reset so a later call can retry the chunk load instead of being stuck
    // with a permanently-rejected promise. Callers are fire-and-forget, so we
    // swallow the error (after logging) and let them no-op.
    fullStoryPromise = null;
    console.error('FullStory failed to load.', error);
    return null;
  }
}

/**
 * Resolves to the FullStory module only if initialization has been started
 * (i.e. the org ID was configured and initializeFullStory() kicked off the
 * dynamic import) AND FullStory is initialized. Otherwise resolves to null so
 * callers no-op — this mirrors the original "return early if not initialized"
 * behavior and, crucially, never loads the FullStory chunk on its own. A
 * failed/rejected import is treated as a safe no-op (null) rather than throwing
 * into the fire-and-forget callers.
 */
async function getInitializedFullStory(): Promise<FullStoryModule | null> {
  if (!fullStoryPromise) return null;
  const FullStoryBrowser = await loadFullStory();
  if (!FullStoryBrowser) return null;
  return FullStoryBrowser.isInitialized() ? FullStoryBrowser : null;
}

export async function initializeFullStory(): Promise<void> {
  if (typeof window === 'undefined') return;

  const orgId = process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID;

  if (!orgId || orgId.includes('__REPLACE')) {
    console.warn('FullStory is not configured. Missing org ID.');
    return;
  }

  const FullStoryBrowser = await loadFullStory();
  if (!FullStoryBrowser) return;

  if (FullStoryBrowser.isInitialized()) return;

  // The init ready callback is the reliable source of the session URL in
  // @fullstory/browser v2 (window.FS.getCurrentSessionURL is a guard stub that
  // returns null until fs.js binds). It fires once, when the session begins.
  FullStoryBrowser.init({ orgId, devMode: false }, ({ sessionUrl }) => {
    void linkFullStorySessionToDatadog(sessionUrl);
  });
}

/**
 * Sets user-scoped properties on the current FullStory session without
 * identifying the user. Use this to attach attributes progressively
 * (e.g. organization name) even on anonymous sessions.
 */
export async function setFullStoryUserProperties(
  properties: Record<string, string | number | boolean>,
): Promise<void> {
  if (typeof window === 'undefined') return;

  const FullStoryBrowser = await getInitializedFullStory();
  if (!FullStoryBrowser) return;

  FullStoryBrowser.FullStory('setProperties', {
    type: 'user',
    properties,
  });
}

/**
 * Identifies the current user in FullStory with their profile data.
 * Should be called after FullStory is initialized and the user is authenticated.
 */
export async function identifyFullStoryUser(profileData: ProfileInfo['data']): Promise<void> {
  if (typeof window === 'undefined') return;

  const FullStoryBrowser = await getInitializedFullStory();
  if (!FullStoryBrowser) return;

  const properties: Record<string, string> = {};

  if (profileData.name) properties.displayName = profileData.name;
  if (profileData.email) properties.email = profileData.email;
  if (profileData.organization?.organization_id) properties.organizationId = profileData.organization.organization_id;
  if (profileData.organization?.name) properties.organizationName = profileData.organization.name;
  if (profileData.roles?.length) properties.roles = profileData.roles.join(',');

  FullStoryBrowser.FullStory('setIdentity', {
    uid: profileData.user_id,
    properties,
  });
}

/**
 * Anonymizes the current FullStory session (e.g. on logout).
 */
export async function anonymizeFullStoryUser(): Promise<void> {
  if (typeof window === 'undefined') return;

  const FullStoryBrowser = await getInitializedFullStory();
  if (!FullStoryBrowser) return;

  FullStoryBrowser.FullStory('setIdentity', { anonymous: true });
}

/**
 * Attaches a FullStory session URL to the Datadog RUM global context so every
 * RUM event — and every RUM error — deep-links to the FullStory replay.
 *
 * Invoked from the FullStory init ready callback (see initializeFullStory),
 * which is the reliable source of the session URL in @fullstory/browser v2: the
 * legacy `window.FS.getCurrentSessionURL` is a guard stub that returns null
 * until `fs.js` binds, so reading it at mount never lands the attribute (why the
 * prior lumi-fe approach recorded zero linked events). Datadog RUM is pulled via
 * the same dynamic import datadog-init.tsx uses (same module singleton), so this
 * never adds the RUM SDK to the main chunk. Best-effort: a failed RUM chunk load
 * is swallowed rather than rejected into the fire-and-forget caller.
 */
export async function linkFullStorySessionToDatadog(sessionUrl: string): Promise<void> {
  if (typeof window === 'undefined' || !sessionUrl) return;

  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    datadogRum.setGlobalContextProperty('fullstory_session_url', sessionUrl);
  } catch (error) {
    console.error('Failed to link FullStory session to Datadog RUM.', error);
  }
}
